import React from 'react';
import { cn } from '@/hen-ui/utils';
import { Image } from '@/hen-ui/atomics/Image';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { imageCardVariants } from './image-card.variants';
import { Flex } from '@/hen-ui/composites/Flex';
import type {
  ImageCardFetchPriority,
  ImageCardLoading,
} from './image-card.types';

export type ImageCardFullWidthProps = {
  /** Image source URL */
  imageUrl: string;
  /** Alt text for the image */
  imageAlt: string;
  /** Optional styling for the image */
  imgClass?: string;
  /** Horizontal alignment of the bottom overlay slot */
  align?: 'left' | 'center';
  /** Defines the content area under the image and supports any valid React node (text, buttons, etc.) */
  children: React.ReactNode;
  /** Optional configuration props (e.g., className, aria-*) for styling and accessibility */
  className?: string;
  /** When the image starts downloading. Defaults to `lazy`. */
  loading?: ImageCardLoading;
  /** Download urgency relative to other images. Omitted by default. */
  fetchPriority?: ImageCardFetchPriority;
};

export const ImageCardFullWidth: React.FC<ImageCardFullWidthProps> = ({
  imageUrl,
  imageAlt,
  imgClass,
  align = 'center',
  children,
  className,
  loading = 'lazy',
  fetchPriority,
}) => {
  return (
    <Flex
      direction='column'
      justify='end'
      className={cn(imageCardVariants({ variant: 'full-width' }), className)}
      role='article'
      aria-label={imageAlt}
      data-hen-id={COMPONENT_IDS.ImageCard}
    >
      {/* Full-size background image */}
      <Image
        src={imageUrl}
        alt={imageAlt}
        loading={loading}
        {...(fetchPriority != null ? { fetchPriority } : {})}
        decoding='async'
        rounded={false}
        objectFit='cover'
        className={cn(
          'absolute inset-0 w-full h-full object-cover z-0',
          imgClass,
        )}
      />
      {/* Bottom-up black gradient overlay for text readability (WCAG compliant) */}
      <div
        className='absolute inset-0 w-full h-full pointer-events-none z-[1] bg-image-card-overlay'
        aria-hidden='true'
      />
      {/* Bottom-anchored overlay content; the parent Flex's justify='end' pins it to the bottom */}
      {children && (
        <Flex
          data-slot='overlay-content'
          direction='column'
          align={align === 'left' ? 'start' : 'center'}
          gap='xs'
          // Span the full card width so horizontal padding is measured from the
          // card edges instead of collapsing to the content width.
          width='full'
          // Temporary fix: mobile horizontal padding is pinned to the `md` token
          // instead of `article-overlay`. The responsive `article-overlay` spacing
          // isn't resolving correctly from tenant config (edudirect-admin) on small
          // viewports, which over-pads the overlay and clips hero/overlay content.
          // Restore a plain `px-article-overlay` once the token resolves correctly
          // across breakpoints.
          className={cn(
            'relative py-article-overlay px-md md:px-article-overlay z-[2] text-white',
            align === 'left' ? 'text-left' : 'text-center',
          )}
        >
          {children}
        </Flex>
      )}
    </Flex>
  );
};
